import { spawn, ChildProcessWithoutNullStreams } from "child_process";
import { EventEmitter } from "events";
import * as path from "path";
import * as url from "url";

// 类型定义
interface LSPMessage {
  jsonrpc: "2.0";
  id?: number | string;
  method?: string;
  params?: any;
  result?: any;
  error?: { code: number; message: string; data?: any };
}

interface FileState {
  content: string;
  languageId: string;
  version: number;
  status: "open" | "change" | "closed";
}

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeout: NodeJS.Timeout;
}

enum LSPClientState {
  STOPPED = "stopped",
  STARTING = "starting",
  RUNNING = "running",
  STOPPING = "stopping",
}

/**
 * Internal configuration for LSP client
 * This interface defines the required structure for the LSP client configuration
 */
interface LSPConfig {
  timeout: number;
  enableFileWatcher: boolean;
  logLevel: string;
  /**
   * Map of language IDs to command arrays
   * First element is the executable path (can be relative or absolute)
   * Remaining elements are command arguments
   */
  lspCommands: Record<string, string[]>;
}

/**
 * User-provided configuration to override default LSP settings
 * This allows users to specify custom paths for language servers
 *
 * @example
 * ```typescript
 * const customConfig: LSPUserConfig = {
 *   timeout: 60000,
 *   lspCommands: {
 *     // Override typescript with absolute path to language server
 *     typescript: ["/usr/local/bin/typescript-language-server", "--stdio"],
 *     python: ["/path/to/custom/pyright-langserver", "--stdio"],
 *   }
 * };
 * const client = new LSPClient(rootUri, customConfig);
 * ```
 */
export interface LSPUserConfig {
  timeout?: number;
  enableFileWatcher?: boolean;
  logLevel?: string;
  /**
   * Map of language IDs to command arrays
   * First element can be an absolute path to the language server executable
   */
  lspCommands?: Record<string, string[]>;
}

const DEFAULT_TIMEOUT = 30_000;
export const DEFAULT_CONFIG: LSPConfig = {
  timeout: DEFAULT_TIMEOUT,
  enableFileWatcher: true,
  logLevel: "info",
  lspCommands: {
    cpp: ["clangd"],
    c: ["clangd", "--pch-storage=memory"],
    python: ["pyright-langserver", "--stdio"],
    typescript: ["typescript-language-server", "--stdio"],
    javascript: ["typescript-language-server", "--stdio"],
  },
};

// 完整的LSP符号类型枚举 (来自Language Server Protocol规范)
export const SymbolKind = {
  File: 1,
  Module: 2,
  Namespace: 3,
  Package: 4,
  Class: 5,
  Method: 6,
  Property: 7,
  Field: 8,
  Constructor: 9,
  Enum: 10,
  Interface: 11,
  Function: 12,
  Variable: 13,
  Constant: 14,
  String: 15,
  Number: 16,
  Boolean: 17,
  Array: 18,
  Object: 19,
  Key: 20,
  Null: 21,
  EnumMember: 22,
  Struct: 23,
  Event: 24,
  Operator: 25,
  TypeParameter: 26,
} as const;

export type SymbolKindType = (typeof SymbolKind)[keyof typeof SymbolKind];

export class LSPClient extends EventEmitter {
  private _state: LSPClientState = LSPClientState.STOPPED;
  private _msgId = 1;
  private _pending = new Map<number, PendingRequest>();
  private _proc: ChildProcessWithoutNullStreams | null = null;
  private _stdoutBuffer = Buffer.alloc(0);
  private _openFiles = new Set<string>();
  private _fileStates = new Map<string, FileState>();
  private _fileVersions = new Map<string, number>();
  private _shutdownSignal = new AbortController();
  private _tasks = new Set<Promise<any>>();
  private _config: LSPConfig;

  /**
   * Creates a new LSP client instance
   *
   * @param _rootUri - The root URI of the project (file:// URI format)
   * @param userConfig - Optional user configuration to override defaults
   *                   - Can include custom absolute paths for language servers
   */
  constructor(
    private _rootUri: string,
    userConfig?: LSPUserConfig,
  ) {
    super();
    // Merge default config with user-provided config
    this._config = {
      ...DEFAULT_CONFIG,
      ...userConfig,
      lspCommands: {
        ...DEFAULT_CONFIG.lspCommands,
        ...(userConfig?.lspCommands || {}),
      },
    };
    this._setupMessageHandlers();
  }

  get state(): LSPClientState {
    return this._state;
  }

  get isClosing(): boolean {
    return [LSPClientState.STOPPING, LSPClientState.STOPPED].includes(
      this._state,
    );
  }

  public async start(lang: string): Promise<void> {
    if (this._state !== LSPClientState.STOPPED) {
      throw new Error(`Cannot start client in state: ${this._state}`);
    }

    this._state = LSPClientState.STARTING;
    try {
      await this._startSubprocess(lang);
      await this._initialize();
      this._state = LSPClientState.RUNNING;
      this.emit("stateChanged", this._state);
    } catch (err) {
      this._state = LSPClientState.STOPPED;
      await this._cleanup();
      throw new Error(`Failed to start LSP client: ${err}`);
    }
  }

  private async _startSubprocess(lang: string): Promise<void> {
    const commands = this._config.lspCommands[lang];
    if (!commands?.length) {
      throw new Error(`No LSP commands configured for language: ${lang}`);
    }

    return new Promise((resolve, reject) => {
      try {
        // The first element in the commands array is the executable path
        // It can be either a command name (which will be resolved in PATH)
        // or an absolute path to the executable (useful for custom installations)
        // Examples:
        // - "typescript-language-server" (resolved via PATH)
        // - "/usr/local/bin/typescript-language-server" (absolute path)
        // - "/custom/path/to/pyright-langserver" (absolute path)
        const executable = commands[0];
        const args = commands.slice(1);

        const proc = spawn(executable, args, {
          stdio: ["pipe", "pipe", "pipe"],
        });

        proc.on("error", (err) => {
          if (this._state === LSPClientState.STARTING) {
            reject(new Error(`Failed to start subprocess: ${err}`));
          }
        });

        proc.stdout.on("data", (chunk) => this._handleData(chunk));
        proc.stderr.on("data", (chunk) => {
          this._handleLogMessage({
            type: 1,
            message: chunk.toString(),
          });
        });

        proc.on("exit", (code) => {
          if (!this.isClosing) {
            this.emit(
              "error",
              new Error(`LSP process crashed with code ${code}`),
            );
          }
        });

        this._proc = proc;
        resolve();
      } catch (err) {
        reject(new Error(`Failed to spawn process: ${err}`));
      }
    });
  }

  private async _initialize(): Promise<void> {
    const initParams = {
      processId: null,
      rootUri: this._rootUri,
      capabilities: {
        textDocument: {
          synchronization: {
            dynamicRegistration: true,
            willSave: true,
            didSave: true,
          },
          completion: { dynamicRegistration: true },
          hover: { dynamicRegistration: true },
          definition: { dynamicRegistration: true },
          references: { dynamicRegistration: true },
          documentSymbol: { dynamicRegistration: true },
        },
        workspace: {
          applyEdit: true,
          workspaceEdit: { documentChanges: true },
          didChangeConfiguration: { dynamicRegistration: true },
          didChangeWatchedFiles: { dynamicRegistration: true },
        },
      },
      workspaceFolders: [{ uri: this._rootUri, name: "workspace" }],
    };

    await this._request("initialize", initParams);
    await this._notify("initialized", {});
  }

  private _setupMessageHandlers(): void {
    this.on("textDocument/publishDiagnostics", (params) =>
      this._handleDiagnostics(params),
    );
    this.on("window/logMessage", (params) => this._handleLogMessage(params));
    this.on("window/showMessage", (params) => this._handleShowMessage(params));
  }

  private _handleData(chunk: Buffer): void {
    this._stdoutBuffer = Buffer.concat([this._stdoutBuffer, chunk]);

    while (true) {
      const headerEnd = this._stdoutBuffer.indexOf("\r\n\r\n");
      if (headerEnd === -1) break;

      const headers = this._stdoutBuffer
        .subarray(0, headerEnd)
        .toString()
        .split("\r\n");
      const contentLengthHeader = headers.find((h) =>
        h.startsWith("Content-Length:"),
      );
      if (!contentLengthHeader) break;

      const contentLength = parseInt(
        contentLengthHeader.split(":")[1].trim(),
        10,
      );
      const messageStart = headerEnd + 4;
      const messageEnd = messageStart + contentLength;

      if (this._stdoutBuffer.length < messageEnd) break;

      const messageData = this._stdoutBuffer.subarray(messageStart, messageEnd);
      this._stdoutBuffer = this._stdoutBuffer.subarray(messageEnd);

      try {
        const message = JSON.parse(messageData.toString()) as LSPMessage;
        this._handleMessage(message);
      } catch (err) {
        this.emit("error", new Error(`Failed to parse message: ${err}`));
      }
    }
  }

  private _handleMessage(message: LSPMessage): void {
    if (message.id !== undefined) {
      const pending = this._pending.get(Number(message.id));
      if (pending) {
        clearTimeout(pending.timeout);
        this._pending.delete(Number(message.id));
        if (message.error) {
          pending.reject(new Error(message.error.message));
        } else {
          pending.resolve(message.result);
        }
      }
    } else if (message.method) {
      this.emit(message.method, message.params);
    }
  }

  private _handleDiagnostics(params: any): void {
    const uri = params.uri;
    const diagnostics = params.diagnostics || [];
    if (diagnostics.length > 0) {
      this.emit("diagnostics", {
        uri,
        diagnostics,
      });
    }
  }

  private _handleLogMessage(params: any): void {
    const message = params.message || "";
    const type = params.type || 3; // Default to INFO
    const levels = ["error", "warn", "info", "debug"];
    const level = levels[Math.min(type - 1, levels.length - 1)] || "info";
    this.emit("log", { level, message });
  }

  private _handleShowMessage(params: any): void {
    const message = params.message || "";
    const type = params.type || 3; // Default to INFO
    this.emit("showMessage", { type, message });
  }

  private async _send(message: LSPMessage): Promise<void> {
    if (!this._proc || !this._proc.stdin) {
      throw new Error("LSP process not available");
    }

    return new Promise((resolve, reject) => {
      const data = JSON.stringify(message);
      const content = `Content-Length: ${Buffer.byteLength(
        data,
      )}\r\n\r\n${data}`;

      this._proc!.stdin.write(content, (err) => {
        if (err) {
          reject(new Error(`Failed to send message: ${err}`));
        } else {
          resolve();
        }
      });
    });
  }

  private async _request(
    method: string,
    params: any,
    timeout: number = this._config.timeout,
  ): Promise<any> {
    if (this._state !== LSPClientState.RUNNING && method !== "initialize") {
      throw new Error(`Cannot send request in state: ${this._state}`);
    }

    const id = this._msgId++;
    const message: LSPMessage = {
      jsonrpc: "2.0",
      id,
      method,
      params,
    };

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pending.delete(id);
        reject(new Error(`Request ${method} timed out`));
      }, timeout);

      this._pending.set(id, {
        resolve,
        reject,
        timeout: timer,
      });

      this._send(message).catch((err) => {
        clearTimeout(timer);
        this._pending.delete(id);
        reject(err);
      });
    });
  }

  private async _notify(method: string, params: any): Promise<void> {
    if (
      this._state !== LSPClientState.RUNNING &&
      !["initialized", "exit"].includes(method)
    ) {
      throw new Error(`Cannot send notification in state: ${this._state}`);
    }

    await this._send({
      jsonrpc: "2.0",
      method,
      params,
    });
  }

  private async _manageFileState(
    uri: string,
    action: "open" | "change" | "close",
    content: string = "",
    languageId: string = "",
  ): Promise<void> {
    if (action === "open") {
      if (this._openFiles.has(uri)) {
        const version = this._fileVersions.get(uri)! + 1;
        this._fileVersions.set(uri, version);
        this._fileStates.set(uri, {
          content,
          languageId,
          version,
          status: "open",
        });
        await this._notify("textDocument/didChange", {
          textDocument: {
            uri,
            version,
          },
          contentChanges: [{ text: content }],
        });
      } else {
        this._openFiles.add(uri);
        this._fileVersions.set(uri, 1);
        this._fileStates.set(uri, {
          content,
          languageId,
          version: 1,
          status: "open",
        });
        await this._notify("textDocument/didOpen", {
          textDocument: {
            uri,
            languageId,
            version: 1,
            text: content,
          },
        });
      }
    } else if (action === "change") {
      if (!this._openFiles.has(uri)) {
        await this._manageFileState(uri, "open", content, languageId);
        return;
      }
      const version = this._fileVersions.get(uri)! + 1;
      this._fileVersions.set(uri, version);
      this._fileStates.set(uri, {
        content,
        languageId,
        version,
        status: "change",
      });
      await this._notify("textDocument/didChange", {
        textDocument: {
          uri,
          version,
        },
        contentChanges: [{ text: content }],
      });
    } else if (action === "close") {
      if (this._openFiles.has(uri)) {
        this._openFiles.delete(uri);
        this._fileVersions.delete(uri);
        const state = this._fileStates.get(uri);
        if (state) {
          this._fileStates.set(uri, {
            ...state,
            status: "closed",
          });
        }
        await this._notify("textDocument/didClose", {
          textDocument: { uri },
        });
      }
    }
  }

  public async readFile(uri: string): Promise<string> {
    const state = this._fileStates.get(uri);
    return state?.content || "";
  }

  public async sendDidOpen(
    uri: string,
    content: string,
    languageId: string,
  ): Promise<void> {
    await this._manageFileState(uri, "open", content, languageId);
  }

  public async sendDidChange(uri: string, content: string): Promise<void> {
    await this._manageFileState(uri, "change", content);
  }

  public async sendDidClose(uri: string): Promise<void> {
    await this._manageFileState(uri, "close");
  }

  public async sendReferences(
    uri: string,
    line: number,
    character: number,
    name: string = "",
    timeout: number = this._config.timeout,
  ): Promise<any> {
    if (line < 0 || character < 0) {
      throw new Error("Line and character must be non-negative");
    }

    const startTime = performance.now();
    const result = await this._request(
      "textDocument/references",
      {
        textDocument: { uri },
        position: { line, character },
        context: { includeDeclaration: false },
      },
      timeout,
    );
    const duration = performance.now() - startTime;

    return {
      uri,
      line,
      character,
      name,
      result,
      duration,
    };
  }

  public async sendDefinition(
    uri: string,
    line: number,
    character: number,
  ): Promise<any> {
    if (line < 0 || character < 0) {
      throw new Error("Line and character must be non-negative");
    }

    return this._request("textDocument/definition", {
      textDocument: { uri },
      position: { line, character },
    });
  }

  public async sendDocumentSymbol(
    uri: string,
    timeout: number = this._config.timeout,
  ): Promise<any> {
    if (!uri) {
      throw new Error("URI is required for documentSymbol");
    }

    return this._request(
      "textDocument/documentSymbol",
      {
        textDocument: { uri },
      },
      timeout,
    );
  }

  public async streamRequests<T>(
    method: (...args: any[]) => Promise<T>,
    argsList: any[][],
    options: {
      maxConcurrency?: number;
      showProgress?: boolean;
      progressEvery?: number;
      progressInterval?: number;
    } = {},
  ): Promise<T[]> {
    const {
      maxConcurrency = 10,
      showProgress = true,
      progressEvery = 10,
      progressInterval = 1000,
    } = options;

    const total = argsList.length;
    const results: (T | null)[] = new Array(total).fill(null);
    let completed = 0;
    let lastPrintTime = 0;
    const startTime = performance.now();

    const semaphore = new Semaphore(maxConcurrency);

    const worker = async (index: number, args: any[]) => {
      await semaphore.acquire();
      try {
        const result = await method(...args);
        results[index] = result;
      } catch (err) {
        this.emit(
          "error",
          new Error(`Request failed at index ${index}: ${err}`),
        );
        results[index] = null;
      } finally {
        completed++;
        semaphore.release();

        if (showProgress) {
          const now = performance.now();
          if (
            completed % progressEvery === 0 ||
            now - lastPrintTime >= progressInterval
          ) {
            lastPrintTime = now;
            const elapsed = (now - startTime) / 1000;
            const speed = completed / elapsed;
            const percent = (completed / total) * 100;
            const eta = (total - completed) / speed;

            this.emit("progress", {
              completed,
              total,
              percent,
              elapsed,
              speed,
              eta,
            });
          }
        }
      }
    };

    const tasks = argsList.map((args, index) => worker(index, args));
    await Promise.all(tasks);

    return results as T[];
  }

  public async shutdown(): Promise<void> {
    // 如果已经在关闭或已停止，直接返回
    if (
      this._state === LSPClientState.STOPPING ||
      this._state === LSPClientState.STOPPED
    ) {
      return;
    }

    const wasRunning = this._state === LSPClientState.RUNNING; // 先保存状态
    this._state = LSPClientState.STOPPING;
    this.emit("stateChanged", this._state);

    try {
      // 只有在 RUNNING 状态才发送 shutdown 请求
      if (wasRunning) {
        try {
          await this._request("shutdown", {}, 5000);
        } catch (err) {
          // 忽略 shutdown 错误，继续执行关闭流程
          this.emit("log", {
            level: "warn",
            message: `Shutdown request failed: ${err}`,
          });
        }
      }

      // 总是发送 exit 通知
      try {
        await this._notify("exit", {});
      } catch (err) {
        this.emit("log", {
          level: "warn",
          message: `Exit notification failed: ${err}`,
        });
      }
    } finally {
      // 确保执行清理
      await this._cancelTasks();
      await this._cleanup();
      this._state = LSPClientState.STOPPED;
      this.emit("stateChanged", this._state);
    }
  }

  private async _cancelTasks(): Promise<void> {
    const tasks = Array.from(this._tasks);
    this._tasks.clear();

    for (const task of tasks) {
      try {
        await Promise.race([
          task,
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Task cancellation timeout")),
              5000,
            ),
          ),
        ]);
      } catch (err) {
        this.emit("error", new Error(`Error during task cancellation: ${err}`));
      }
    }
  }

  private async _cleanup(): Promise<void> {
    if (this._proc) {
      // Close stdin
      if (this._proc.stdin && !this._proc.stdin.destroyed) {
        this._proc.stdin.end();
      }

      // Kill process if still running
      if (this._proc.exitCode === null) {
        this._proc.kill();
      }

      this._proc = null;
    }

    // Clean up state
    this._openFiles.clear();
    this._fileStates.clear();
    this._fileVersions.clear();
    this._pending.clear();
    this._stdoutBuffer = Buffer.alloc(0);
  }

  public getRelativePath(uri: string): string {
    const localPath = url.fileURLToPath(uri);
    const rootPath = url.fileURLToPath(this._rootUri);
    return path.relative(rootPath, localPath);
  }

  public async sendHover(
    uri: string,
    position: { line: number; character: number },
    timeout: number = this._config.timeout,
  ): Promise<any> {
    if (position.line < 0 || position.character < 0) {
      throw new Error("Line and character must be non-negative");
    }
    return this._request(
      "textDocument/hover",
      {
        textDocument: { uri },
        position,
      },
      timeout,
    );
  }

  /**
   * 对单个文件进行 LSP 诊断，返回 diagnostics 数组
   */
  public async diagnoseFile(
    uri: string,
    content: string,
    languageId: string,
    waitMs = 800,
  ): Promise<any[]> {
    const diagnostics: any[] = [];
    const seen = new Set<string>();
    const handler = ({
      uri: diagUri,
      diagnostics: diags,
    }: {
      uri: string;
      diagnostics: any[];
    }) => {
      if (diagUri !== uri) return;
      for (const d of diags) {
        const key = `${d.range?.start?.line}:${d.range?.start?.character}:${d.message}`;
        if (seen.has(key)) continue;
        seen.add(key);
        diagnostics.push(d);
      }
    };
    this.on("diagnostics", handler);
    await this.sendDidOpen(uri, content, languageId);
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    await this.sendDidClose(uri);
    this.off("diagnostics", handler);
    return diagnostics;
  }
}

// Helper class for concurrency control
class Semaphore {
  private _queue: (() => void)[] = [];
  private _count: number;

  constructor(count: number) {
    this._count = count;
  }

  acquire(): Promise<void> {
    return new Promise((resolve) => {
      if (this._count > 0) {
        this._count--;
        resolve();
      } else {
        this._queue.push(resolve);
      }
    });
  }

  release(): void {
    this._count++;
    const next = this._queue.shift();
    if (next) next();
  }
}
